// reportBuilder.js — Rich PDF report builder (thin orchestrator).
// Helper modules:
//   ./reportBuilder/utils.js          — text / currency / filter helpers + title page + page header
//   ./reportBuilder/charts.js         — bar / line / donut chart drawing
//   ./reportBuilder/insights.js       — AI Insights + AI Recommendations footer per page
//   ./reportBuilder/savingsReport.js  — standalone Savings Potential PDF (re-exported below)
//   ./reportBuilder/pages/*.js        — one renderer per page; registered in pages/index.js
import jsPDF from 'jspdf';
import {
  PAGE_LABELS,
  getSavedFilters, filtersToParams, filtersDescription,
  getCurrencySymbol,
  renderFilterBanner, renderTitlePage, renderSectionHeader,
} from './reportBuilder/utils';
import { renderInsights } from './reportBuilder/insights';
import { PAGE_RENDERERS } from './reportBuilder/pages';
import { applyUnicodeFontSupport } from './reportBuilder/fonts';

// Re-exports for backwards compatibility with existing imports across the app.
export { getSavedFilters };
export { buildSavingsReport } from './reportBuilder/savingsReport';

// --- Main builder ---

export async function buildRichReport(api, reportName, selectedPages, pageFilters = {}, orgName = '') {
  const doc = new jsPDF();
  await applyUnicodeFontSupport(doc, api, reportName);
  const pw = doc.internal.pageSize.getWidth();
  const cs = getCurrencySymbol();

  let y = renderTitlePage(doc, reportName, selectedPages.length, orgName);

  for (let pi = 0; pi < selectedPages.length; pi++) {
    const page = selectedPages[pi];
    if (pi > 0) { doc.addPage(); y = 15; }

    const filters = pageFilters[page] || getSavedFilters(page);
    const qs = filtersToParams(filters);
    const filterDesc = filtersDescription(filters);

    y = renderSectionHeader(doc, y, PAGE_LABELS[page] || page);
    y = renderFilterBanner(doc, y, filterDesc);

    // Dispatch to the page-specific renderer.
    let aiInsights = [];
    let aiRecommendations = [];
    const renderer = PAGE_RENDERERS[page];
    if (renderer) {
      try {
        const result = await renderer(api, doc, { y, qs, filterDesc, cs, pw, page, filters });
        y = result.y;
        aiInsights = result.aiInsights || [];
        aiRecommendations = result.aiRecommendations || [];
      } catch (err) {
        console.error(`Failed to render report page ${page}:`, err);
      }
    } else {
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`(no renderer for page: ${page})`, 14, y);
      y += 6;
    }

    // AI Insights + Recommendations footer (always last).
    y = renderInsights(doc, y, aiInsights, aiRecommendations);
  }

  // Trigger download safely (sanitise filename, defer cleanup).
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  const safeName = (reportName || 'report').replace(/[^\w\-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'report';
  const dateTag = new Date().toISOString().slice(0, 10);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeName}_${dateTag}.pdf`;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    try { document.body.removeChild(link); URL.revokeObjectURL(url); } catch { /* noop */ }
  }, 1500);

  return pdfBlob;
}
